import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { CockpitAreaGate } from "@/components/cockpit/CockpitAreaGate";
import { CockpitPageShell } from "@/components/cockpit/CockpitPageShell";
import { CockpitPageHeader } from "@/components/cockpit/CockpitPageHeader";
import { CockpitSection } from "@/components/cockpit/CockpitSection";
import { useCockpitSession } from "@/hooks/use-cockpit-session";
import { supportInquirySchema, type SupportInquiryInput } from "@/lib/inquiries";
import { submitSupportInquiry } from "@/lib/support.functions";

export const Route = createFileRoute("/_cockpit/cockpit/support")({
  head: () => ({
    meta: [
      { title: "Support – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <CockpitAreaGate area="support">
      <CockpitSupportPage />
    </CockpitAreaGate>
  ),
});

function CockpitSupportPage() {
  const session = useCockpitSession();
  const submit = useServerFn(submitSupportInquiry);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SupportInquiryInput>({
    resolver: zodResolver(supportInquirySchema),
    defaultValues: { subject: "", message: "", phone: "" },
  });

  const accountName =
    [session.firstName, session.lastName].filter(Boolean).join(" ") || "Ihr Konto";

  const onSubmit = async (values: SupportInquiryInput) => {
    setSubmitError(null);
    try {
      await submit({ data: values });
      reset({ subject: "", message: "", phone: "" });
      setSent(true);
      toast.success("Ihre Anfrage ist bei uns eingegangen.");
    } catch (error) {
      // Eingaben bleiben stehen – nichts muss neu getippt werden.
      const message =
        error instanceof Error && error.message && !/Forbidden/i.test(error.message)
          ? error.message
          : "Ihre Anfrage wurde nicht übernommen. Ihre Eingaben sind erhalten – bitte senden Sie erneut ab.";
      setSubmitError(message);
      toast.error("Senden fehlgeschlagen.");
    }
  };

  const fieldClass =
    "mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const labelClass = "block text-sm font-medium text-foreground";

  return (
    <CockpitPageShell width="reading">
      <CockpitPageHeader
        title="Support"
        description="Sie haben eine Frage zu Ihrem Kurs oder Ihrem Zugang? Schreiben Sie uns – wir antworten Ihnen per E-Mail an die Adresse Ihres Kontos."
      />

      <CockpitSection
        label="Kontakt"
        title="Anfrage an unser Team"
        intro={`Wir melden uns bei ${accountName} unter ${session.email ?? "Ihrer Konto-E-Mail-Adresse"}.`}
      >
        {sent ? (
          <div className="rounded-md border border-border bg-muted/40 p-6">
            <p className="flex items-center gap-2 font-display text-base font-extrabold text-primary">
              <CheckCircle2 className="h-5 w-5" aria-hidden />
              Vielen Dank – Ihre Anfrage ist eingegangen.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Unsere Antwort erhalten Sie an{" "}
              {session.email ?? "die E-Mail-Adresse Ihres Kontos"}. In der Regel
              melden wir uns innerhalb eines Werktags.
            </p>
            <button
              type="button"
              onClick={() => setSent(false)}
              className="mt-5 inline-flex items-center justify-center rounded-sm bg-brand-button px-5 py-2.5 font-display text-sm font-extrabold text-foreground transition-opacity hover:opacity-90"
            >
              Weitere Anfrage stellen
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
            <div>
              <label htmlFor="support-subject" className={labelClass}>
                Betreff *
              </label>
              <input
                id="support-subject"
                type="text"
                className={fieldClass}
                aria-invalid={!!errors.subject}
                {...register("subject")}
              />
              {errors.subject && (
                <p className="mt-2 text-xs text-destructive">{errors.subject.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="support-message" className={labelClass}>
                Nachricht *
              </label>
              <textarea
                id="support-message"
                rows={7}
                className={`${fieldClass} resize-none`}
                aria-invalid={!!errors.message}
                {...register("message")}
              />
              {errors.message && (
                <p className="mt-2 text-xs text-destructive">{errors.message.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="support-phone" className={labelClass}>
                Rückrufnummer (freiwillig)
              </label>
              <input
                id="support-phone"
                type="tel"
                autoComplete="tel"
                className={fieldClass}
                aria-invalid={!!errors.phone}
                {...register("phone")}
              />
              {errors.phone && (
                <p className="mt-2 text-xs text-destructive">{errors.phone.message}</p>
              )}
            </div>

            {submitError && (
              <p className="text-sm text-destructive" role="alert">
                {submitError}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {isSubmitting ? "Wird gesendet…" : "Anfrage senden"}
            </button>
          </form>
        )}
      </CockpitSection>
    </CockpitPageShell>
  );
}
